import { Card, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { IAllUserNode } from '../../../types/users'
import { ChannelConfigData } from '../../channelConfig/hooks/useChannelConfig'
import { URL as BACKEND_URL } from '../../../gql/client'

interface Props {
  node: IAllUserNode
  orgConfig: Partial<ChannelConfigData>
  loadingOrgConfig: boolean
  orgId: string | undefined
}

export const OrganizationChannelsTab: React.FC<Props> = ({
  node,
  orgConfig,
  loadingOrgConfig,
  orgId,
}) => {
  const WebhookLine = ({ label, url }: { label: string; url: string }) => {
    return (
      <div className='row g-2 mt-2 align-items-center p-2 bg-light border rounded'>
        <span className='fs-8 fw-semibold col-auto'>{label}</span>
        <code className='col text-dark fs-7 text-break'>{url}</code>
        <button
          type='button'
          className='btn btn-sm btn-primary p-1 col-auto'
          onClick={() => {
            navigator.clipboard.writeText(url)
            toast.success('Webhook URL copied to clipboard')
          }}
          title='Copy full webhook URL'
          style={{ minWidth: '28px' }}
        >
          <i className='bi bi-clipboard' />
        </button>
      </div>
    )
  }

  if (!orgId) {
    return (
      <Card className='mb-0'>
        <Card.Body className='text-center py-10'>
          <i className='bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block' />
          <h5 className='text-gray-700'>No Organization Assigned</h5>
          <p className='text-muted mb-0'>
            This user must be assigned to an organization to view channel settings.
          </p>
        </Card.Body>
      </Card>
    )
  }

  if (loadingOrgConfig) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 280 }}>
        <Spinner animation='border' variant='primary' />
      </div>
    )
  }

  const baseUrl = BACKEND_URL

  return (
    <>
      <div className='mb-5'>
        <p className='text-muted fs-7 mb-0'>
          These settings apply to all agents in the organization.
        </p>
      </div>

      <div className='row g-3'>
        {/* ─── Twilio SMS ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-chat-dots fs-3 text-info' />
                  <div>
                    <div className='fw-bold text-gray-900'>Twilio SMS</div>
                    <div className='text-muted fs-8'>SMS messaging integration</div>
                  </div>
                </div>
              </div>
              {orgConfig.twilio_enabled && orgConfig.twilio_phone_number && (
                <>
                  <div className='text-muted fs-8 mb-2'>Phone Number</div>
                  <div className='fw-bold text-gray-900 mb-3'>{orgConfig.twilio_phone_number}</div>
                  {orgConfig.webhook_token && (
                    <>
                      <WebhookLine
                        label='Inbound Messages:'
                        url={`${baseUrl}/webhooks/twilio/sms/${orgConfig.webhook_token}/`}
                      />
                      <div className='row g-2 mt-2 align-items-center p-2 bg-warning border rounded'>
                        <span className='fs-8 fw-semibold col-auto'>Status Callbacks:</span>
                        <code className='col text-dark fs-7 text-break'>{`${baseUrl}/webhooks/twilio/status/`}</code>
                        <button
                          type='button'
                          className='btn btn-sm btn-warning p-1 col-auto'
                          onClick={() => {
                            navigator.clipboard.writeText(`${baseUrl}/webhooks/twilio/status/`)
                            toast.success('Status callback URL copied')
                          }}
                          title='Copy status callback URL'
                          style={{ minWidth: '28px' }}
                        >
                          <i className='bi bi-clipboard' />
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
              {!orgConfig.twilio_enabled && (
                <div className='text-muted fs-8'>Not configured</div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── WhatsApp Integration ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-whatsapp fs-3 text-success' />
                  <div>
                    <div className='fw-bold text-gray-900'>WhatsApp</div>
                    <div className='text-muted fs-8'>{orgConfig.whatsapp_provider || 'Not configured'}</div>
                  </div>
                </div>
              </div>
              {orgConfig.whatsapp_provider && orgConfig.whatsapp_provider !== 'NONE' && (
                <>
                  {orgConfig.whatsapp_provider === 'TWILIO' && orgConfig.twilio_whatsapp_number && (
                    <>
                      <div className='text-muted fs-8 mb-2'>WhatsApp Number</div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.twilio_whatsapp_number}</div>
                      {orgConfig.webhook_token && (
                        <>
                          <WebhookLine
                            label='Inbound Messages:'
                            url={`${baseUrl}/webhooks/twilio/whatsapp/${orgConfig.webhook_token}/`}
                          />
                          <div className='row g-2 mt-2 align-items-center p-2 bg-warning border rounded'>
                            <span className='fs-8 fw-semibold col-auto'>Status Callbacks:</span>
                            <code className='col text-dark fs-7 text-break'>{`${baseUrl}/webhooks/twilio/status/`}</code>
                            <button
                              type='button'
                              className='btn btn-sm btn-warning p-1 col-auto'
                              onClick={() => {
                                navigator.clipboard.writeText(`${baseUrl}/webhooks/twilio/status/`)
                                toast.success('Status callback URL copied')
                              }}
                              title='Copy status callback URL'
                              style={{ minWidth: '28px' }}
                            >
                              <i className='bi bi-clipboard' />
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {orgConfig.whatsapp_provider === 'BOTMAKER' && orgConfig.botmaker_phone_line_number && (
                    <>
                      <div className='text-muted fs-8 mb-2'>Botmaker Phone Line</div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.botmaker_phone_line_number}</div>
                      {orgConfig.webhook_token && (
                        <WebhookLine
                          label='Webhook URL (Botmaker):'
                          url={`${baseUrl}/webhooks/botmaker/${orgConfig.webhook_token}/`}
                        />
                      )}
                    </>
                  )}
                  {orgConfig.whatsapp_provider === 'META' && orgConfig.meta_whatsapp_phone_number_id && (
                    <>
                      <div className='text-muted fs-8 mb-2'>Meta Phone Number ID</div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.meta_whatsapp_phone_number_id}</div>
                      {orgConfig.webhook_token && (
                        <WebhookLine
                          label='Webhook URL (Meta):'
                          url={`${baseUrl}/webhooks/meta/${orgConfig.webhook_token}/`}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── Email SMTP ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-envelope fs-3 text-warning' />
                  <div>
                    <div className='fw-bold text-gray-900'>Email SMTP</div>
                    <div className='text-muted fs-8'>Email messaging integration</div>
                  </div>
                </div>
              </div>
              {orgConfig.email_enabled && orgConfig.email_host && (
                <>
                  <div className='text-muted fs-8 mb-2'>SMTP Host</div>
                  <div className='fw-bold text-gray-900 mb-1'>{orgConfig.email_host}</div>
                  <div className='text-muted fs-8 mb-3'>Port: {orgConfig.email_port || 587} {orgConfig.email_use_tls && '(TLS)'}</div>
                  <div className='text-muted fs-8 mb-2'>From Address</div>
                  <div className='fw-bold text-gray-900'>{orgConfig.email_from || 'Not set'}</div>
                </>
              )}
              {!orgConfig.email_enabled && (
                <div className='text-muted fs-8'>Not configured</div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── PolicyDial ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <img src='/media/tfi/policydial-icon.svg' alt='PolicyDial' style={{ width: '28px', height: '28px' }} />
                  <div>
                    <div className='fw-bold text-gray-900'>PolicyDial</div>
                    <div className='text-muted fs-8'>VICIdial integration</div>
                  </div>
                </div>
              </div>
              {orgConfig.vicidial_enabled ? (
                <>
                  <div className='text-muted fs-8 mb-2'>Base URL</div>
                  <div className='fw-bold text-gray-900 mb-3'>{orgConfig.vicidial_base_url || 'Not set'}</div>
                  {orgConfig.vicidial_campaign_id && (
                    <>
                      <div className='text-muted fs-8 mb-2'>Campaign ID</div>
                      <div className='fw-bold text-gray-900'>{orgConfig.vicidial_campaign_id}</div>
                    </>
                  )}
                </>
              ) : (
                <div className='text-muted fs-8'>Not configured</div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  )
}
